import React, { useState } from "react";
import {
  Modal,
  Form,
  Tag,
  InputNumber,
  Button,
  Space,
  Tabs,
  Card,
  Row,
  Col,
  Tooltip,
  Typography,
  Divider,
  Input,
  Alert,
  Steps,
  Result,
  message,
  Flex,
  Grid,
} from "antd";
import { RechargeProduct, User } from "../../../contexts/GlobalContext";
import { payment } from "../../../utils/payment";

const { Text } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

interface RechargeModalProps {
  visible: boolean;
  onCancel: () => void;
  rechargeType: "member" | "token";
  rechargeProducts: RechargeProduct[];
  rewardProducts: RechargeProduct[];
  selectedProductId?: number;
  setSelectedProductId: (id: number) => void;
  tokenAmount: number;
  setTokenAmount: (amount: number) => void;
  handleAlipayPay: (
    productId: number,
    productCount: number,
    projectId?: number
  ) => void;
  handleWechatPay: (
    productId: number,
    productCount: number,
    projectId?: number
  ) => void;
  user: User;
  onRedeemSubmit: () => Promise<void>;
  redeemForm: any;
}

// 充值码响应接口
interface RedeemResponse {
  success_code: string;
  recharge_token: string;
  token: string;
  fail_list:
    | {
        fail_code: string;
        recharge_token: string;
        msg: string;
      }[]
    | null;
}

const RechargeModal: React.FC<RechargeModalProps> = ({
  visible,
  onCancel,
  rechargeType,
  rechargeProducts,
  rewardProducts,
  selectedProductId,
  setSelectedProductId,
  tokenAmount,
  setTokenAmount,
  handleAlipayPay,
  handleWechatPay,
  user,
  redeemForm,
  onRedeemSubmit,
}) => {
  const [activeTab, setActiveTab] = useState("plans");
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemResult, setRedeemResult] = useState<RedeemResponse | null>(null);
  const [redeemStep, setRedeemStep] = useState<0 | 1 | 2>(1);
  const [wechatPayModalVisible, setWechatPayModalVisible] = useState(false);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // 获取当前选中的产品
  const currentProduct = selectedProductId
    ? rechargeProducts.find((p) => p.id === selectedProductId)
    : undefined;

  // 计算总价格
  const totalPrice = currentProduct
    ? ((currentProduct.price || 0) / 100) *
      (rechargeType === "token" ? tokenAmount : 1)
    : 0;

  // 计算原价（如果有折扣）
  const originalPrice = currentProduct?.original_price
    ? (currentProduct.original_price / 100) *
      (rechargeType === "token" ? tokenAmount : 1)
    : null;

  // 计算折扣百分比（如果有原价）
  const discountPercent =
    originalPrice && originalPrice > 0
      ? Math.round((1 - totalPrice / originalPrice) * 100)
      : null;

  // 获取奖励产品（如果有）
  const getRewardProduct = () => {
    if (
      !currentProduct?.reward_product ||
      currentProduct.reward_product === "none"
    ) {
      return null;
    }

    return rewardProducts.find(
      (p) => String(p.id) === currentProduct.reward_product
    );
  };

  const rewardProduct = getRewardProduct();

  // 修复处理兑换码提交的函数
  const handleRedeemSubmit = async () => {
    try {
      const values = await redeemForm.validateFields();
      setRedeemLoading(true);

      // 将输入的兑换码按行分割并过滤空行
      const redeemCodes = values.redeemCodes
        .split("\n")
        .map((code: string) => code.trim())
        .filter((code: string) => code);

      try {
        const authToken = localStorage.getItem("jwt_token");

        if (!authToken) {
          message.error("登录已过期，请重新登录");
          setRedeemLoading(false);
          return;
        }

        // 调用兑换码API
        const result = await payment.redeemCode(
          { recharge_code: redeemCodes },
          authToken
        );

        if (result) {
          setRedeemResult(result);
          setRedeemStep(2);

          if (result.fail_list && result.fail_list.length > 0) {
            if (result.recharge_token && parseInt(result.recharge_token) > 0) {
              message.warning(`部分兑换码兑换成功，部分失败`);
              setTimeout(() => window.location.reload(), 1500);
            } else {
              message.error(`兑换失败，所有兑换码均无效`);
            }
          } else {
            message.success(
              `兑换成功，您的账户已增加 ${result.recharge_token} tokens`
            );
            setTimeout(() => window.location.reload(), 1500);
          }
        } else {
          message.error("兑换失败，请检查兑换码是否正确");
        }
      } catch (error: any) {
        console.error("兑换失败:", error);
        message.error(error?.response?.data?.msg || "兑换失败，请重试");
      } finally {
        setRedeemLoading(false);
      }
    } catch (error) {
      console.error("表单验证失败:", error);
      setRedeemLoading(false);
    }
  };

  // 重置兑换流程
  const resetRedeemProcess = () => {
    setRedeemStep(1);
    setRedeemResult(null);
    redeemForm.resetFields();
  };

  return (
    <Modal
      getContainer={false}
      title={
        <div style={{ display: "flex", alignItems: "center" }}>
          <i
            className={
              rechargeType === "member" ? "ri-vip-crown-fill" : "ri-coins-fill"
            }
            style={{
              fontSize: isMobile ? 18 : 20,
              marginRight: 12,
              color: rechargeType === "member" ? "#faad14" : "#52c41a",
            }}
          />
          <span>{rechargeType === "member" ? "会员充值" : "Token充值"}</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={isMobile ? "95%" : 650}
      styles={{
        body: { padding: isMobile ? "0 12px 16px" : "0 24px 24px" },
        header: { padding: isMobile ? "16px 16px 8px" : undefined },
      }}
      className="recharge-modal"
      destroyOnClose
    >
      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          // 切换到兑换码标签页时重置表单
          if (key === "redeem") {
            resetRedeemProcess();
          }
        }}
        centered
        size={isMobile ? "small" : "middle"}
        items={[
          {
            key: "plans",
            label: (
              <Space size={isMobile ? 4 : 8}>
                <i className="ri-price-tag-3-line" />
                买套餐
              </Space>
            ),
            children: (
              <>
                <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]}>
                  {rechargeProducts.map((product) => (
                    <Col xs={24} sm={12} key={product.id}>
                      <Card
                        className={`product-card ${
                          selectedProductId === product.id
                            ? "product-card-selected"
                            : ""
                        } hover-yellow`}
                        onClick={() => setSelectedProductId(product.id)}
                        styles={{
                          body: { padding: isMobile ? 12 : 16 },
                        }}
                        style={{
                          border:
                            selectedProductId === product.id
                              ? "1px solid #1890ff"
                              : "1px solid #f0f0f0",
                          position: "relative",
                          transition: "all 0.3s",
                          cursor: "pointer",
                        }}
                      >
                        {selectedProductId === product.id && (
                          <div
                            style={{
                              position: "absolute",
                              top: -10,
                              left: -10,
                              background: "#1890ff",
                              borderRadius: "50%",
                              width: 24,
                              height: 24,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                              fontSize: 14,
                            }}
                          >
                            <i className="ri-check-line" />
                          </div>
                        )}

                        <div style={{ marginBottom: 8 }}>
                          {product.discount_desc &&
                            product.discount_type !== "none" && (
                              <Tag
                                color="red"
                                style={{
                                  marginRight: 8,
                                  borderRadius: 4,
                                  fontSize: isMobile ? 12 : 14,
                                }}
                              >
                                {product.discount_desc}
                              </Tag>
                            )}
                          <Text strong style={{ fontSize: isMobile ? 14 : 16 }}>
                            {product.name}
                          </Text>
                        </div>

                        {product.token_amount && (
                          <div
                            style={{
                              color: "#389e0d",
                              fontSize: isMobile ? 12 : 14,
                              marginBottom: 8,
                            }}
                          >
                            <i
                              className="ri-coin-line"
                              style={{ marginRight: 4 }}
                            />
                            <Text type="success">
                              {product.token_amount.toLocaleString()} Token
                            </Text>
                          </div>
                        )}

                        <div
                          style={{ display: "flex", alignItems: "flex-end" }}
                        >
                          <Text
                            style={{
                              color: "#ff4d4f",
                              fontSize: isMobile ? 16 : 20,
                              fontWeight: "bold",
                            }}
                          >
                            ¥{((product.price || 0) / 100).toFixed(2)}
                          </Text>

                          {product.original_price && (
                            <Text
                              delete
                              type="secondary"
                              style={{
                                marginLeft: 8,
                                fontSize: isMobile ? 12 : 14,
                              }}
                            >
                              ¥
                              {((product.original_price || 0) / 100).toFixed(2)}
                            </Text>
                          )}
                        </div>

                        {product.description && (
                          <div
                            style={{
                              marginTop: 8,
                              fontSize: 12,
                              color: "#888",
                              height: isMobile ? 32 : 36,
                              overflow: "hidden",
                            }}
                          >
                            {product.description}
                          </div>
                        )}
                      </Card>
                    </Col>
                  ))}
                </Row>

                {rechargeType === "token" && selectedProductId && (
                  <Flex
                    vertical={isMobile}
                    justify={isMobile ? "start" : "space-between"}
                    align={isMobile ? "start" : "middle"}
                    style={{
                      marginTop: 16,
                      background: "#f9f9f9",
                      padding: isMobile ? "12px" : "16px",
                      borderRadius: 8,
                      gap: isMobile ? 12 : 0,
                    }}
                  >
                    <Flex align="center">
                      <Text strong>购买数量：</Text>
                      <Tooltip title="设置购买数量">
                        <InputNumber
                          min={1}
                          value={tokenAmount}
                          onChange={(value) => setTokenAmount(value || 1)}
                          style={{
                            marginLeft: 8,
                            width: isMobile ? 80 : 100,
                          }}
                          addonAfter="份"
                          size={isMobile ? "small" : "middle"}
                        />
                      </Tooltip>
                    </Flex>
                    <Flex align="center">
                      <Text
                        type="secondary"
                        style={{ fontSize: isMobile ? 12 : 14 }}
                      >
                        单价：¥{((currentProduct?.price || 0) / 100).toFixed(2)}{" "}
                        × {tokenAmount} =
                      </Text>
                      <Text
                        strong
                        style={{
                          marginLeft: 8,
                          fontSize: isMobile ? 14 : 16,
                          color: "#ff4d4f",
                        }}
                      >
                        ¥{totalPrice.toFixed(2)}
                      </Text>
                    </Flex>
                  </Flex>
                )}

                {user.apiInfo?.invite.invite_reward_valid && rewardProduct && (
                  <div
                    style={{
                      marginTop: 16,
                      background: "#fffbe6",
                      border: "1px solid #ffe58f",
                      padding: isMobile ? "10px 12px" : "12px 16px",
                      borderRadius: "8px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <i
                        className="ri-gift-fill"
                        style={{
                          color: "#faad14",
                          fontSize: isMobile ? 16 : 20,
                          marginRight: 8,
                        }}
                      />
                      <Text
                        strong
                        style={{
                          color: "#d48806",
                          fontSize: isMobile ? 14 : 16,
                        }}
                      >
                        邀请福利
                      </Text>
                    </div>
                    <div
                      style={{
                        marginTop: 8,
                        paddingLeft: isMobile ? 24 : 28,
                        fontSize: isMobile ? 12 : 14,
                      }}
                    >
                      <Text style={{ color: "#85662c" }}>
                        您是受邀用户，在{" "}
                        {user.apiInfo?.invite.invite_remaining_days}{" "}
                        天内购买此商品双方额外获得：
                      </Text>
                      <div style={{ marginTop: 8 }}>
                        <Tag color="#d48806" style={{ padding: "4px 8px" }}>
                          <i
                            className="ri-award-fill"
                            style={{ marginRight: 4 }}
                          />
                          {rewardProduct.name}
                        </Tag>
                        {rewardProduct.description && (
                          <div
                            style={{
                              marginTop: 8,
                              color: "#85662c",
                              fontSize: isMobile ? 12 : 13,
                            }}
                          >
                            {rewardProduct.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <Divider style={{ margin: isMobile ? "16px 0" : undefined }} />

                <Flex
                  style={{
                    background: "rgb(249, 249, 249)",
                    borderRadius: "8px",
                    padding: isMobile ? "12px" : "16px",
                    marginBottom: isMobile ? "16px" : "20px",
                  }}
                  justify="space-between"
                  align="center"
                >
                  <Text strong style={{ fontSize: isMobile ? 14 : 16 }}>
                    价格合计：
                  </Text>
                  <Flex align="center">
                    <Text
                      style={{
                        color: "#ff4d4f",
                        fontSize: isMobile ? 18 : 24,
                        fontWeight: "bold",
                      }}
                    >
                      ¥{totalPrice.toFixed(2)}
                    </Text>

                    {originalPrice && (
                      <Text
                        delete
                        type="secondary"
                        style={{ marginLeft: 8, fontSize: isMobile ? 12 : 14 }}
                      >
                        ¥{originalPrice.toFixed(2)}
                      </Text>
                    )}

                    {discountPercent && (
                      <Tag
                        color="volcano"
                        style={{ marginLeft: 8, fontSize: isMobile ? 12 : 14 }}
                      >
                        省{discountPercent}%
                      </Tag>
                    )}
                  </Flex>
                </Flex>

                {/* 优化按钮区域 */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "12px",
                    marginBottom: isMobile ? "16px" : "24px",
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    type="primary"
                    size={isMobile ? "middle" : "large"}
                    onClick={() =>
                      handleAlipayPay(
                        selectedProductId || 0,
                        rechargeType === "token" ? tokenAmount : 1
                      )
                    }
                    disabled={!selectedProductId}
                    icon={
                      <i
                        className="ri-alipay-line"
                        style={{ fontSize: isMobile ? 14 : 16 }}
                      />
                    }
                    style={{
                      minWidth: isMobile ? "120px" : "140px",
                      height: isMobile ? "40px" : "48px",
                      fontSize: isMobile ? "14px" : "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                    }}
                  >
                    支付宝支付
                  </Button>

                  <Button
                    type="primary"
                    size={isMobile ? "middle" : "large"}
                    onClick={() => {
                      if (isMobile) {
                        setWechatPayModalVisible(true);
                      } else {
                        handleWechatPay(
                          selectedProductId || 0,
                          rechargeType === "token" ? tokenAmount : 1
                        );
                      }
                    }}
                    disabled={!selectedProductId}
                    icon={
                      <i
                        className="ri-wechat-pay-line"
                        style={{ fontSize: isMobile ? 14 : 16 }}
                      />
                    }
                    style={{
                      minWidth: isMobile ? "120px" : "140px",
                      height: isMobile ? "40px" : "48px",
                      fontSize: isMobile ? "14px" : "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                      backgroundColor: "#09bb07",
                      borderColor: "#09bb07",
                    }}
                  >
                    微信支付
                  </Button>
                  <Modal
                    title="微信支付提醒"
                    open={wechatPayModalVisible}
                    onOk={() => setWechatPayModalVisible(false)}
                    onCancel={() => setWechatPayModalVisible(false)}
                    okText="确定"
                  >
                    <p>由于微信支付政策限制，微信支付功能仅支持在电脑端使用。</p>
                    <p>请在电脑浏览器中打开本网页，然后使用微信支付功能。</p>
                  </Modal>
                </div>

                <div
                  style={{
                    fontSize: "12px",
                    color: "#999",
                    textAlign: "center",
                    background: "#f9f9f9",
                    padding: isMobile ? "6px" : "8px",
                    borderRadius: "4px",
                  }}
                >
                  <i
                    className="ri-shield-check-line"
                    style={{ marginRight: 4 }}
                  />
                  点击支付表示同意
                  <a
                    href="https://postchat.zhheo.com/infovip.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#1677ff", marginLeft: 4 }}
                  >
                    《会员服务协议》
                  </a>
                </div>
              </>
            ),
          },
          {
            key: "redeem",
            label: (
              <Space size={isMobile ? 4 : 8}>
                <i className="ri-coupon-line" />
                兑换码
              </Space>
            ),
            children: (
              <div>
                <Card
                  variant="outlined"
                  style={{
                    marginBottom: isMobile ? 16 : 24,
                    boxShadow: "none",
                    padding: isMobile ? 0 : undefined,
                  }}
                >
                  <Steps
                    size="small"
                    current={redeemStep}
                    responsive={!isMobile}
                    direction={isMobile ? "vertical" : "horizontal"}
                    items={[
                      {
                        title: "获取兑换码",
                        description: "购买获取",
                        icon: <i className="ri-shopping-cart-line" />,
                      },
                      {
                        title: "输入兑换码",
                        description: redeemStep === 1 ? "当前步骤" : "",
                        icon: <i className="ri-input-method-line" />,
                        status: redeemStep === 1 ? "process" : "finish",
                      },
                      {
                        title: "兑换成功",
                        description: redeemStep === 2 ? "当前步骤" : "",
                        icon: <i className="ri-check-double-line" />,
                        status: redeemStep === 2 ? "finish" : "wait",
                      },
                    ]}
                  />
                </Card>

                {redeemStep === 1 && (
                  <>
                    <Form form={redeemForm} onFinish={onRedeemSubmit}>
                      <Form.Item
                        name="redeemCodes"
                        label={
                          <div style={{ marginBottom: isMobile ? 6 : 8 }}>
                            <Space size={isMobile ? 4 : 8}>
                              <i
                                className="ri-coupon-line"
                                style={{ color: "#722ed1" }}
                              />
                              <span style={{ fontSize: isMobile ? 13 : 14 }}>
                                请输入兑换码（一行一个）
                              </span>
                            </Space>
                          </div>
                        }
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                        rules={[
                          { required: true, message: "请输入至少一个兑换码" },
                          {
                            validator: (_, value) => {
                              if (!value) return Promise.resolve();
                              const codes = value
                                .split("\n")
                                .filter((code: string) => code.trim());
                              if (codes.length === 0) {
                                return Promise.reject(
                                  "请输入至少一个有效的兑换码"
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <TextArea
                          placeholder="请输入兑换码，每行一个"
                          autoSize={{ minRows: isMobile ? 3 : 4, maxRows: 8 }}
                          style={{ fontFamily: "monospace" }}
                        />
                      </Form.Item>

                      <div
                        style={{
                          marginTop: isMobile ? 16 : 24,
                          display: "flex",
                          justifyContent: "space-between",
                          flexDirection: isMobile ? "column" : "row",
                          gap: isMobile ? 16 : 0,
                        }}
                      >
                        <Button
                          type="link"
                          icon={
                            <i
                              className="ri-store-line"
                              style={{ marginRight: 4 }}
                            />
                          }
                          onClick={() =>
                            window.open("https://store.zhheo.com", "_blank")
                          }
                          style={{ padding: 0 }}
                        >
                          前往HeoStore购买兑换码
                        </Button>

                        <Space>
                          <Button
                            onClick={() => setActiveTab("plans")}
                            size={isMobile ? "small" : "middle"}
                          >
                            返回
                          </Button>
                          <Button
                            type="primary"
                            onClick={handleRedeemSubmit}
                            loading={redeemLoading}
                            icon={
                              <i
                                className="ri-coupon-3-line"
                                style={{ marginRight: 4 }}
                              />
                            }
                            size={isMobile ? "small" : "middle"}
                          >
                            立即兑换
                          </Button>
                        </Space>
                      </div>
                    </Form>

                    <div
                      style={{
                        marginTop: isMobile ? 16 : 20,
                        background: "#f6f6f6",
                        borderRadius: 8,
                        padding: isMobile ? "10px 12px" : "12px 16px",
                        fontSize: isMobile ? 12 : 13,
                        color: "#666",
                      }}
                    >
                      <Space align="start" size={isMobile ? 8 : 12}>
                        <i
                          className="ri-question-line"
                          style={{
                            color: "#1890ff",
                            fontSize: isMobile ? 14 : 16,
                            marginTop: 2,
                          }}
                        />
                        <span>
                          如果兑换码无效或遇到问题，请确认兑换码是否输入正确，或联系
                          <a
                            href="mailto:zhheo@qq.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1890ff" }}
                          >
                            zhheo@qq.com
                          </a>
                          。 兑换成功后将自动为您的账户充值相应的会员或Token。
                        </span>
                      </Space>
                    </div>
                  </>
                )}

                {redeemStep === 2 && redeemResult && (
                  <div>
                    <Result
                      status="success"
                      title="兑换成功"
                      subTitle={
                        <div>
                          <div>成功充值 {redeemResult.recharge_token}</div>
                          <div>当前账户余额: {redeemResult.token} </div>
                        </div>
                      }
                    />

                    {redeemResult.fail_list &&
                      redeemResult.fail_list.length > 0 && (
                        <Alert
                          type="warning"
                          message="以下兑换码兑换失败"
                          description={
                            <div>
                              {redeemResult.fail_list.map((item, index) => (
                                <div
                                  key={index}
                                  style={{ marginBottom: "8px" }}
                                >
                                  <Tag color="orange">{item.fail_code}</Tag>
                                  <Text type="danger">{item.msg}</Text>
                                </div>
                              ))}
                            </div>
                          }
                          showIcon
                          style={{ marginBottom: 16 }}
                        />
                      )}

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: isMobile ? "12px" : "16px",
                        marginTop: isMobile ? "16px" : "20px",
                      }}
                    >
                      <Button
                        type="primary"
                        onClick={() => onCancel()}
                        size={isMobile ? "small" : "middle"}
                      >
                        完成
                      </Button>
                      <Button
                        onClick={resetRedeemProcess}
                        size={isMobile ? "small" : "middle"}
                      >
                        继续兑换
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ),
          },
        ]}
      />
    </Modal>
  );
};

export default RechargeModal;
